(ns io.daskul.webapp.widgets.billing.customer-picker
  "Generic customer picker for billing — searches /billing/customers endpoint.

   Search-as-you-type Autocomplete. Returns the selected customer map via
   :on-change (or nil when cleared/edited).

   Built on Mantine Autocomplete (not Select) on purpose: the input's text IS
   the value, so there is no separate value/searchValue for Mantine to
   reconcile — which is what made the old Select drop characters mid-typing and
   fail to show a preset customer (#832). Display text is computed synchronously,
   mirroring widgets/form/selector.cljs :autocomplete.

   Props:
   - :value        — customer-id string (controlled selection)
   - :on-change    — (fn [customer-map | nil]) called on selection/clear
                      customer-map: {:customer-type :customer-id :customer-name}
   - :initial-name — optional customer-name shown for a preset :value before a
                      search resolves its label (prepopulation from
                      Unbilled → Create Invoice, #832)
   - :label        — optional label (default: \"Customer\")
   - :placeholder  — optional placeholder
   - :disabled     — optional boolean"
  (:require
   [uix.core :as uix :refer [defui $]]
   [io.daskul.webapp.api.http :as api]
   [io.daskul.webapp.ui :as ui]
   [io.daskul.webapp.ui.icons :as icons]))

(defn- fetch-customers
  "Search billing customers endpoint. Calls on-result with items vector.
   Routes through api/request! (shared base URL + auth + 401 policy)."
  [q on-result on-error]
  (api/request!
   {:method :get
    :path "/billing/customers"
    :params (when (seq q) {:q q})
    :on-success (fn [resp] (on-result (:items resp)))
    :on-error (fn [_] (on-error))}))

(defn- customer-label
  "Human option label for a customer: \"Name (type)\"."
  [{:keys [customer-type customer-name]}]
  (str customer-name " (" customer-type ")"))

(defui customer-picker
  [{:keys [value on-change label placeholder disabled initial-name]}]
  (let [[customers set-customers!] (uix/use-state [])
        [loading? set-loading!] (uix/use-state false)
        [text set-text!] (uix/use-state "")
        just-selected (uix/use-ref false)
        timer-ref (uix/use-ref nil)

        do-search
        (fn [q]
          (set-loading! true)
          (fetch-customers q
                           (fn [items]
                             (set-customers! items)
                             (set-loading! false))
                           (fn [] (set-loading! false))))

        ;; Label of the currently-selected customer — resolved from search
        ;; results when present, else the :initial-name passed for a preset
        ;; :value (before any search has run).
        selected-label
        (when value
          (or (some #(when (= (str (:customer-id %)) value) (customer-label %))
                    customers)
              initial-name))

        ;; Synchronous display: the selected label while a value is set,
        ;; otherwise the user's typed text. No effect timing gap, so typing is
        ;; never clobbered by an async data/label reconcile.
        display (if (and value selected-label) selected-label text)

        handle-change
        (fn [val]
          (set-text! val)
          (if @just-selected
            ;; A selection just set the text — consume it, don't re-search.
            (reset! just-selected false)
            (do
              ;; Debounce the server search.
              (when-let [t @timer-ref] (js/clearTimeout t))
              (reset! timer-ref (js/setTimeout #(do-search val) 300))
              ;; Editing the text drops the current selection (like selector).
              (when value (on-change nil)))))

        handle-submit
        (fn [label-str]
          (reset! just-selected true)
          (set-text! label-str)
          (when-let [c (some #(when (= (customer-label %) label-str) %) customers)]
            (on-change c)))

        handle-clear
        (fn [_]
          (set-text! "")
          (reset! just-selected false)
          (on-change nil))]

    ;; Initial load (no search term — shows all customers up to limit).
    ;; Inlined rather than calling do-search so the mount-only effect needs no
    ;; dependency on that closure (run-once, empty deps).
    (uix/use-effect
     (fn []
       (set-loading! true)
       (fetch-customers ""
                        (fn [items]
                          (set-customers! items)
                          (set-loading! false))
                        (fn [] (set-loading! false)))
       js/undefined)
     [])

    ($ ui/autocomplete
      {:label (or label "Customer")
       :placeholder (cond
                      loading? "Loading..."
                      placeholder placeholder
                      :else "Search for a customer...")
       :value display
       :data (clj->js (mapv customer-label customers))
       :onChange handle-change
       :onOptionSubmit handle-submit
       ;; Never disable on `loading?`: the debounced search toggles it, and a
       ;; disabled input drops focus mid-typing (the "abrupt unfocus"). The
       ;; search runs in the background; the field stays live.
       :disabled disabled
       :rightSectionPointerEvents "all"
       :rightSection (when (and (not disabled) (seq display))
                       ($ ui/action-icon
                         {:variant "subtle" :color "gray" :size "sm"
                          :aria-label "Clear customer"
                          :onClick handle-clear}
                         ($ icons/icon-x {:size 16})))})))
